import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { fetchForecastMonthToDate } from '@src/api/actions/dashboard/forecast';
import { daysInPeriod, localWhatIf } from './forecastMath';

// What a client's own users see (``view === "client"`` payloads). Leads and
// cost per lead are the part we state plainly; appointments, sales and revenue
// ride on the client's own funnel and are always framed as conditional. No
// cost of marketing, recommendations or campaign internals belong here.

export const NOT_AVAILABLE_MESSAGE = 'Forecast is not yet available for your account.';

export const DISCLAIMER =
  'These are estimates for budget planning, not a guarantee of leads, appointments or revenue — ' +
  'results depend on market conditions and on factors outside our control, including speed to lead, ' +
  'confirmation, in-home sales and pricing.';

const money = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;
const count = (v) =>
  v == null || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Round a range outward to a friendly grain so it never reads falsely precise.
function grainFor(v) {
  if (v >= 1000) return 50;
  if (v >= 100) return 10;
  if (v >= 20) return 5;
  return 1;
}
const roundDown = (v) => {
  const g = grainFor(v);
  return Math.max(0, Math.floor(v / g) * g);
};
const roundUp = (v) => {
  const g = grainFor(v);
  return Math.ceil(v / g) * g;
};

function range(lo, hi, fmt) {
  const a = Number(lo) || 0;
  const b = Number(hi) || 0;
  const low = roundDown(Math.min(a, b));
  const high = roundUp(Math.max(a, b));
  if (low === high) return fmt(low);
  return `${fmt(low)}–${fmt(high)}`;
}

function moneyRange(lo, hi) {
  const a = Number(lo) || 0;
  const b = Number(hi) || 0;
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  const g = high >= 10000 ? 1000 : 5;
  const l = Math.floor(low / g) * g;
  const h = Math.ceil(high / g) * g;
  return l === h ? money(l) : `${money(l)}–${Math.round(h).toLocaleString()}`;
}

function monthName(period) {
  if (!period) return '';
  return new Date(`${period}T00:00:00`).toLocaleDateString(undefined, { month: 'long' });
}

function monthYear(period) {
  if (!period) return '';
  return new Date(`${period}T00:00:00`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function sliderBounds(seg) {
  const base = Number(seg.base_budget) || 0;
  const floor = Math.max(0, Math.round(Math.min(seg.spend_floor ?? base * 0.5, base)));
  const ceiling = Math.round(Math.max(seg.spend_ceiling ?? base * 2, base));
  const step = Math.max(50, Math.round((ceiling - floor) / 100));
  return { floor, ceiling, step };
}

function revenueBasis(segments) {
  const bases = (segments || []).map((s) => s.revenue_basis || 'none');
  return {
    anyBenchmark: bases.includes('vertical_benchmark'),
    anyRevenue: bases.some((b) => b !== 'none'),
  };
}

function BudgetRow({ seg, value, onChange, showLabel }) {
  const { floor, ceiling, step } = sliderBounds(seg);
  return (
    <Box sx={{ minWidth: 0 }}>
      {showLabel && (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
            {seg.scope_label || seg.vertical || 'Budget'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {money(value)}/mo
          </Typography>
        </Stack>
      )}
      <Box sx={{ px: 1 }}>
        <Slider
          value={Math.min(Math.max(value, floor), ceiling)}
          min={floor}
          max={ceiling}
          step={step}
          onChange={(_e, v) => onChange(seg.budget_id, Array.isArray(v) ? v[0] : v)}
          valueLabelDisplay="auto"
          valueLabelFormat={money}
          aria-label={`Monthly budget for ${seg.scope_label || 'this service'}`}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">{money(floor)}</Typography>
        <Typography variant="caption" color="text.secondary">{money(ceiling)}</Typography>
      </Stack>
    </Box>
  );
}

function NextMonth({ data }) {
  const segments = useMemo(() => data?.segments || [], [data]);
  const days = daysInPeriod(data?.period);
  const [budgets, setBudgets] = useState({});

  useEffect(() => {
    const init = {};
    segments.forEach((s) => {
      init[s.budget_id] = s.base_budget;
    });
    setBudgets(init);
  }, [segments]);

  const dirty = segments.some(
    (s) => budgets[s.budget_id] != null && Number(budgets[s.budget_id]) !== Number(s.base_budget)
  );
  const scenarios = useMemo(
    () => (dirty ? localWhatIf(segments, budgets, days).scenarios : data?.scenarios || {}),
    [dirty, segments, budgets, days, data]
  );

  const lo = scenarios.low || {};
  const exp = scenarios.expected || {};
  const hi = scenarios.high || {};
  const total = exp.spend ?? segments.reduce((sum, s) => sum + (Number(budgets[s.budget_id] ?? s.base_budget) || 0), 0);
  // The pessimistic scenario carries the pricier lead.
  const cplHigh = lo.leads ? lo.spend / lo.leads : null;
  const cplLow = hi.leads ? hi.spend / hi.leads : null;

  const { anyBenchmark, anyRevenue } = revenueBasis(segments);
  const showAppts = Number(hi.appointments) > 0;
  const showSales = Number(hi.booked) > 0;
  const showRevenue = anyRevenue && Number(hi.net_revenue) > 0;
  const onChange = (id, v) => setBudgets((prev) => ({ ...prev, [id]: v }));

  if (!segments.length) {
    return (
      <Alert severity="info">
        There&apos;s no budget set for {monthYear(data?.period)} yet, so there&apos;s nothing to forecast.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Your total investment for the month
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {money(total)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Ad spend and our fee together — move the {segments.length > 1 ? 'sliders' : 'slider'} to try a
          different budget.
        </Typography>
        <Stack spacing={2} divider={segments.length > 1 ? <Divider flexItem /> : null}>
          {segments.map((seg) => (
            <BudgetRow
              key={seg.budget_id}
              seg={seg}
              value={budgets[seg.budget_id] ?? seg.base_budget}
              onChange={onChange}
              showLabel={segments.length > 1}
            />
          ))}
        </Stack>
      </Paper>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
          With {money(total)} you can expect about {range(lo.leads, hi.leads, count)} leads
          {cplLow && cplHigh ? ` at ${moneyRange(cplLow, cplHigh)} per lead` : ''}.
        </Typography>
      </Box>

      {(showAppts || showSales || showRevenue) && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            What those leads could turn into
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {anyBenchmark
              ? 'At the rates we see across your industry, these leads could become:'
              : 'At the rates we see in your own results, these leads could become:'}
          </Typography>
          <Grid container spacing={1.5}>
            {showAppts && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Appointments</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {range(lo.appointments, hi.appointments, count)}
                </Typography>
              </Grid>
            )}
            {showSales && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Sales</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {range(lo.booked, hi.booked, count)}
                </Typography>
              </Grid>
            )}
            {showRevenue && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Revenue</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {moneyRange(lo.net_revenue, hi.net_revenue)}
                </Typography>
              </Grid>
            )}
          </Grid>
          {anyBenchmark && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Based on typical rates in your industry — share your sales numbers and we&apos;ll use yours.
            </Typography>
          )}
        </Paper>
      )}

      {(data?.seasonal_factor > 1.01 || data?.new_client) && (
        <Stack spacing={0.5}>
          {data?.seasonal_factor > 1.01 && (
            <Typography variant="body2" color="text.secondary">
              {monthName(data.period)} costs more to advertise in; this forecast already includes that.
            </Typography>
          )}
          {data?.new_client && (
            <Typography variant="body2" color="text.secondary">
              You&apos;re in your first weeks, so this range is wider and will tighten as results come in.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function MetricCard({ label, forecast, band, soFar, pace }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        <Typography variant="body2">
          Forecast: <b>{forecast}</b>
          {band ? <Typography component="span" variant="caption" color="text.secondary"> ({band})</Typography> : null}
        </Typography>
        <Typography variant="body2">So far: <b>{soFar}</b></Typography>
        <Typography variant="body2">On pace for: <b>{pace}</b></Typography>
      </Stack>
    </Paper>
  );
}

function ThisMonth({ clientId, showRevenue }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchForecastMonthToDate({ client_id: clientId, view: 'client' })
      .then((resp) => !cancelled && setData(resp?.data || resp))
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(
          err?.response?.status === 403 ? NOT_AVAILABLE_MESSAGE : 'Could not load this month right now.'
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return null;

  const f = data.forecast || {};
  const exp = f.expected || {};
  const lo = f.low || {};
  const hi = f.high || {};
  const act = data.actual_mtd || {};
  const proj = data.projected_month || {};
  const cpl = (o) => (o?.leads ? o.spend / o.leads : null);

  const rows = [
    {
      key: 'leads', label: 'Leads', fmt: count,
      band: lo.leads != null ? range(lo.leads, hi.leads, count) : '',
    },
    {
      key: 'cpl', label: 'Cost per lead', fmt: money, value: cpl,
      band: cpl(lo) && cpl(hi) ? moneyRange(cpl(hi), cpl(lo)) : '',
    },
    { key: 'spend', label: 'Total investment', fmt: money, band: '' },
    {
      key: 'appointments', label: 'Appointments', fmt: count,
      band: lo.appointments != null ? range(lo.appointments, hi.appointments, count) : '',
      hide: !(Number(hi.appointments) > 0 || Number(act.appointments) > 0),
    },
    {
      key: 'booked', label: 'Sales', fmt: count,
      band: lo.booked != null ? range(lo.booked, hi.booked, count) : '',
      hide: !(Number(hi.booked) > 0 || Number(act.booked) > 0),
    },
    {
      key: 'net_revenue', label: 'Revenue', fmt: money,
      band: lo.net_revenue != null ? moneyRange(lo.net_revenue, hi.net_revenue) : '',
      hide: !showRevenue,
    },
  ].filter((r) => !r.hide);

  const val = (row, src) => (row.value ? row.value(src) : src?.[row.key]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {monthYear(data.period)} — day {data.days_elapsed} of {data.days_in_month}. The forecast is the
        one we made before the month began.
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map((row) => (
          <Grid item xs={12} sm={6} md={4} key={row.key}>
            <MetricCard
              label={row.label}
              forecast={row.fmt(val(row, exp))}
              band={row.band}
              soFar={row.fmt(val(row, act))}
              pace={row.fmt(val(row, proj))}
            />
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary">
        &quot;On pace for&quot; extends the month so far to the full month. Sales and revenue are often
        reported a few weeks after the appointment, so mid-month they usually read lower than they finish.
      </Typography>
    </Stack>
  );
}

export default function ClientForecastView({ data, clientId, title }) {
  const [tab, setTab] = useState('next');
  const { anyRevenue } = revenueBasis(data?.segments);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%', minWidth: 0, overflowX: 'hidden' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>
        {title || `${monthYear(data?.period)} forecast`}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="fullWidth"
      >
        <Tab value="next" label={monthName(data?.period) || 'Next month'} />
        <Tab value="mtd" label="This month" />
      </Tabs>

      {tab === 'next' ? <NextMonth data={data} /> : <ThisMonth clientId={clientId} showRevenue={anyRevenue} />}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
      >
        {DISCLAIMER}
      </Typography>
    </Box>
  );
}
